import { useState } from 'react'
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  type MultiFactorError,
} from 'firebase/auth'
import { auth, appleProvider, googleProvider } from '@/firebase'
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
} from '@/components/ui/responsive-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SecondStepChallenge } from '@/components/auth/SecondStepChallenge'
import { TwoStepMessages } from '@/constants/messages'
import { reportAuthFailure } from '@/services/authFailureLog'
import { emailOwner } from '@/services/accountEmail'
import { isSecondStepRequired } from '@/services/twoFactor'

function errorCode(error: unknown): string | undefined {
  return (error as { code?: string })?.code
}

/**
 * Re-checks the user (password, or Google/Apple again), passing through the
 * second step when they already have one, then reruns the action. Firebase
 * asks for this (`auth/requires-recent-login`) before changing two-step
 * methods or connecting a sign-in method (PB-62, PB-71).
 */
export function ConfirmIdentityDialog({
  action,
  onClose,
}: {
  action: null | (() => Promise<void>)
  onClose: () => void
}) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [secondStep, setSecondStep] = useState<MultiFactorError | null>(null)
  const user = auth.currentUser
  const owner = emailOwner(user)

  const close = () => {
    setPassword('')
    setError(null)
    setSecondStep(null)
    onClose()
  }

  const rerun = async () => {
    try {
      await action?.()
      close()
    } catch (err) {
      reportAuthFailure('confirm_identity', err)
      setError(TwoStepMessages.failed(errorCode(err)))
    }
  }

  const reauth = async (run: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await run()
      await rerun()
    } catch (err) {
      if (isSecondStepRequired(err)) {
        setSecondStep(err)
      } else {
        reportAuthFailure('confirm_identity', err)
        const c = errorCode(err)
        setError(
          c === 'auth/wrong-password' || c === 'auth/invalid-credential'
            ? TwoStepMessages.wrongPassword
            : TwoStepMessages.failed(c)
        )
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={!!action} onOpenChange={o => !o && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{TwoStepMessages.passwordTitle}</DialogTitle>
          <DialogDescription>
            {owner === 'password'
              ? TwoStepMessages.passwordPrompt
              : TwoStepMessages.googlePrompt}
          </DialogDescription>
        </DialogHeader>
        {secondStep ? (
          <SecondStepChallenge
            error={secondStep}
            onDone={() => rerun()}
            onCancel={close}
          />
        ) : owner === 'password' ? (
          <form
            onSubmit={e => {
              e.preventDefault()
              if (!user?.email) return
              reauth(() =>
                reauthenticateWithCredential(
                  user,
                  EmailAuthProvider.credential(user.email as string, password)
                )
              )
            }}
            className="grid gap-4"
          >
            <div className="grid gap-2">
              <Label htmlFor="confirm-password">Password</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                {TwoStepMessages.cancel}
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? TwoStepMessages.checking : 'Confirm'}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="grid gap-3">
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button
              disabled={busy || !user}
              onClick={() =>
                user &&
                reauth(() =>
                  reauthenticateWithPopup(
                    user,
                    owner === 'apple.com' ? appleProvider : googleProvider
                  )
                )
              }
            >
              {TwoStepMessages.continueWithGoogle}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
